import { addVisible } from 'shared/addVisible';
import { Layer, MapRef, useMapInstance } from 'shared/map-exports';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { useMemo } from 'react';
import { VisibilityToggledSource } from 'features/mapping/components/layers/VisibilityToggledSource';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { LatLng } from 'shared/types';
import { useWaterLevel } from 'features/waterLevel/hooks/useWaterLevel';
import { useSelectedWaterLevelId } from 'features/waterLevel/hooks/useSelectedWaterLevelId';
import { useAuthState } from 'state/useAuthState';
import { history } from 'shared/history';
import { useTranslation } from 'react-i18next';
import { usePulseAnimation } from 'hooks/usePulseAnimation';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import { FloodCategory } from '../../waterLevel.types';
import {
  WATER_LEVEL_LABELS_LAYER_ID,
  WATER_LEVEL_SOURCE_ID,
  SOURCE_LAYER,
  WATER_LEVEL_NONE_OFFLINE_ICONS_LAYER_ID,
  WATER_LEVEL_LOW_ZOOM_LAYER_ID,
  WATER_LEVEL_HIGH_ZOOM_LAYER_ID,
  WATER_LEVEL_SELECTED_PULSE_LAYER_ID,
  MAX_ICON_ZOOM_LEVEL,
} from './WaterLevelLayer.constants';
import {
  getLabelStyle,
  getLowZoomIconStyle,
  getHighZoomIconStyle,
  getNoneOfflineIconStyle,
  getSelectedPointIconStyle,
  pulseBaseRadiusExpression,
} from './WaterLevelLayer.styles';

type WaterLevelLayerProps = {
  visible: boolean;
};

type WaterLevelFeatureProperties = {
  name: string;
  weather_station_type: string;
  flood_category: FloodCategory;
  noaa_lid: string;
  usgs_id: string;
  level: number;
  flow?: number;
  is_offline: boolean;
  is_observation_recent: boolean;
  peak_forecast_flood_category?: FloodCategory;
  has_flood_categories: boolean;
};

const REFRESH_INTERVAL_SECONDS = 60 * 15; // 15 minutes

const getFeatureCoordinates = (input: {
  mapInstance?: MapRef;
  sourceId: string;
  featureId: number;
}): LatLng | null => {
  const { mapInstance, sourceId, featureId } = input;

  if (!mapInstance) {
    return null;
  }

  const features = mapInstance.querySourceFeatures(sourceId, {
    sourceLayer: SOURCE_LAYER,
  });

  const feature = features.find((f) => f.id === featureId);

  if (feature?.geometry.type === 'Point') {
    const [lng, lat] = feature.geometry.coordinates;
    return { lng, lat };
  }

  return null;
};

export const resolveWaterLevelPath = (
  geoJsonFeatures: MapboxFeature<WaterLevelFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const featureId = feature.id;
  if (featureId === undefined || featureId === null) return null;
  return `/gauge/${featureId}`;
};

export const waterLevelClickHandler: MapClickHandler<
  WaterLevelFeatureProperties
> = (geoJsonFeatures) => {
  const path = resolveWaterLevelPath(geoJsonFeatures);
  if (path) history.push(path, { skipZoom: true });
};

export const WaterLevelLayer = (props: WaterLevelLayerProps): JSX.Element => {
  const { visible } = props;
  const activeGaugeId = useSelectedWaterLevelId();
  const map = useMapInstance();
  const { waterLevel } = useWaterLevel({ id: activeGaugeId });
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const { t } = useTranslation();

  const activeGaugeLatLng = useMemo(() => {
    if (!activeGaugeId) return null;
    const latLngFromFeature = getFeatureCoordinates({
      mapInstance: map,
      sourceId: WATER_LEVEL_SOURCE_ID,
      featureId: activeGaugeId,
    });
    if (!latLngFromFeature && waterLevel?.location) {
      return waterLevel.location;
    }
    return latLngFromFeature;
  }, [activeGaugeId, map, waterLevel?.location]);

  useCenterMap({
    latLng: activeGaugeLatLng,
    drawerIsOpen: true,
    defaultSnapPointPct: 0.47,
    zoom: MAX_ICON_ZOOM_LEVEL,
  });

  usePulseAnimation({
    baseRadius: pulseBaseRadiusExpression,
    isActive: Boolean(activeGaugeId),
    layerId: WATER_LEVEL_SELECTED_PULSE_LAYER_ID,
    map: map?.getMap(),
    maxZoomLevel: 13,
    minZoomLevel: MAX_ICON_ZOOM_LEVEL,
  });

  return (
    <VisibilityToggledSource
      id={WATER_LEVEL_SOURCE_ID}
      visible={visible}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
      url={getWatchdutyTileUrl(SOURCE_LAYER)}
    >
      <Layer
        id={WATER_LEVEL_SELECTED_PULSE_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getSelectedPointIconStyle(activeGaugeId), visible)}
      />
      <Layer
        id={WATER_LEVEL_LOW_ZOOM_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getLowZoomIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WATER_LEVEL_HIGH_ZOOM_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getHighZoomIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WATER_LEVEL_NONE_OFFLINE_ICONS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getNoneOfflineIconStyle(isInternalUser), visible)}
      />
      <Layer
        id={WATER_LEVEL_LABELS_LAYER_ID}
        beforeId={OVERLAY_ICONS_BEFORE_ID}
        {...addVisible(getLabelStyle(isInternalUser, t), visible)}
      />
    </VisibilityToggledSource>
  );
};
